<template>
  <div class="gjs-drill">
    <div v-if="loading" class="gjs-drill-msg">
      <v-progress-circular indeterminate size="18" width="2" color="primary"></v-progress-circular>
    </div>
    <div v-else-if="error" class="gjs-drill-msg gjs-drill-error">{{ error }}</div>
    <div v-else-if="payload">
      <div v-if="payload.message" class="gjs-drill-msg" :class="{ 'gjs-drill-error': payload.too_many_rows }">
        {{ payload.message }}
      </div>
      <table v-if="lines.length" class="gjs-drill-table">
        <thead>
          <tr>
            <th>{{ t("date") }}</th>
            <th>{{ t("reference") }}</th>
            <th>{{ t("leafCode") }}</th>
            <th>{{ t("leafName") }}</th>
            <th>{{ t("description") }}</th>
            <th class="gjs-num">{{ c("debit") }}</th>
            <th class="gjs-num">{{ c("credit") }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="line in lines" :key="line.line_id">
            <td class="gjs-nowrap">{{ line.journal_date }}</td>
            <td class="gjs-nowrap gjs-ltr">
              {{ line.reference }}
              <span v-if="line.is_reversal" class="gjs-flag">{{ t("reversal") }}</span>
              <span v-if="line.is_reversed" class="gjs-flag">{{ t("reversed") }}</span>
            </td>
            <td class="gjs-nowrap gjs-ltr">{{ line.account_code }}</td>
            <td>{{ line.account_name }}</td>
            <td class="gjs-desc">{{ line.description }}</td>
            <td class="gjs-num">{{ line.debit ? formatMoney(line.debit) : "" }}</td>
            <td class="gjs-num">{{ line.credit ? formatMoney(line.credit) : "" }}</td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td colspan="5">{{ c("total") }} ({{ t("linesCount") }}: {{ payload.totals.lines_count }})</td>
            <td class="gjs-num">{{ formatMoney(payload.totals.debit) }}</td>
            <td class="gjs-num">{{ formatMoney(payload.totals.credit) }}</td>
          </tr>
        </tfoot>
      </table>
      <div v-if="lines.length" class="gjs-drill-pager">
        <template v-if="!showingAll && lastPage > 1">
          <v-btn small flat :disabled="page <= 1" @click="load(page - 1)">{{ t("previous") }}</v-btn>
          <span>{{ $t("finance.reports2.generalJournalSummary.pageOf", { page: page, last: lastPage }) }}</span>
          <v-btn small flat :disabled="page >= lastPage" @click="load(page + 1)">{{ t("next") }}</v-btn>
        </template>
        <v-btn v-if="!showingAll && lastPage > 1" small flat color="primary" @click="showAll">{{ c("showAll") }}</v-btn>
      </div>
    </div>
  </div>
</template>

<script>
/*
 * "+" drill of report 2.1 (general journal summary): the journal lines under
 * one summary row (an account, or every leaf of a rolled-up party parent;
 * optionally one day and one side). 50 lines per page, "show all" up to the
 * server cap of 10,000.
 * API: this.api.ACTIONS.ACCOUNTING_GENERAL_JOURNAL_SUMMARY_ENTRIES.
 */
export default {
  name: "GeneralJournalSummaryEntries",
  props: {
    // { account_id, from, to, currency, date?, side?, roll_up_parties }
    params: { type: Object, required: true },
  },
  data() {
    return {
      loading: false,
      error: null,
      payload: null,
      page: 1,
      showingAll: false,
    };
  },
  computed: {
    lines() {
      return (this.payload && this.payload.lines) || [];
    },
    lastPage() {
      return (this.payload && this.payload.pagination && this.payload.pagination.last_page) || 1;
    },
    // The parent rebuilds the params object on every render: reload only
    // when the VALUES change.
    paramsKey() {
      return JSON.stringify(this.params || {});
    },
  },
  watch: {
    paramsKey(next, previous) {
      if (next === previous) {
        return;
      }
      this.showingAll = false;
      this.load(1);
    },
  },
  mounted() {
    this.load(1);
  },
  methods: {
    t(key) {
      return this.$t("finance.reports2.generalJournalSummary." + key);
    },
    c(key) {
      return this.$t("finance.reports2.common." + key);
    },
    requestParams(page) {
      const params = {};
      Object.keys(this.params || {}).forEach((key) => {
        const value = this.params[key];
        if (value !== null && value !== undefined && value !== "") {
          params[key] = value;
        }
      });
      if (this.showingAll) {
        params.per_page = "all";
      } else {
        params.page = page;
        params.per_page = 50;
      }
      return params;
    },
    load(page) {
      this.loading = true;
      this.error = null;
      this.axios
        .get(this.api.ACTIONS.ACCOUNTING_GENERAL_JOURNAL_SUMMARY_ENTRIES, { params: this.requestParams(page) })
        .then((response) => {
          this.payload = response.data.data || null;
          this.page = (this.payload && this.payload.pagination && this.payload.pagination.page) || 1;
        })
        .catch((error) => {
          this.error = this.messageOf(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    showAll() {
      this.showingAll = true;
      this.load(1);
    },
    messageOf(error) {
      const data = error && error.response && error.response.data;
      if (data && data.errors && typeof data.errors === "object") {
        const first = Object.keys(data.errors)[0];
        if (first && data.errors[first] && data.errors[first][0]) {
          return data.errors[first][0];
        }
      }
      return (data && data.message) || this.t("failedLoadEntries");
    },
  },
};
</script>

<style scoped>
.gjs-drill {
  padding: 6px 4px 10px;
  background: #fafbfc;
}
.gjs-drill-msg {
  padding: 8px 12px;
  color: rgba(0, 0, 0, 0.6);
}
.gjs-drill-error {
  color: #c62828;
}
.gjs-drill-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  background: #fff;
}
.gjs-drill-table th,
.gjs-drill-table td {
  border: 1px solid rgba(0, 0, 0, 0.15);
  padding: 4px 8px;
  text-align: start;
}
.gjs-drill-table thead th {
  background: #f1f3f5;
  font-weight: 700;
}
.gjs-drill-table tfoot td {
  font-weight: 700;
  background: #f5f5f5;
}
.gjs-num {
  text-align: end !important;
  direction: ltr;
  unicode-bidi: embed;
  white-space: nowrap;
}
.gjs-nowrap {
  white-space: nowrap;
}
.gjs-ltr {
  direction: ltr;
  unicode-bidi: embed;
}
.gjs-desc {
  white-space: normal;
  min-width: 180px;
}
.gjs-flag {
  display: inline-block;
  margin-inline-start: 4px;
  padding: 0 4px;
  border-radius: 3px;
  font-size: 10px;
  background: #ffe0b2;
  color: #e65100;
}
.gjs-drill-pager {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-top: 6px;
  font-size: 12px;
}
</style>
